import React from 'react';
import Markdown from 'react-markdown';
import { copyToClipboard } from '@/utils/helpers';

interface TranscriptionPreviewModalProps {
  visible: boolean;
  content: string;
  fileName: string;
  onClose: () => void;
}

/**
 * 转录文本预览弹窗 - Notion 风格
 * 参考 Result.tsx 的设计语言
 */
const TranscriptionPreviewModal: React.FC<TranscriptionPreviewModalProps> = ({
  visible,
  content,
  fileName,
  onClose,
}) => {
  if (!visible) return null;

  const handleCopy = () => {
    if (!content) return;
    copyToClipboard(content, '已复制到剪贴板');
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={onClose}>
      {/* 遮罩 */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" />

      {/* 弹窗主体 */}
      <div
        className="relative bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] border border-[#E9E9E7] w-[720px] max-w-[90vw] max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 顶部标题栏 */}
        <div className="flex items-center justify-between px-7 pt-6 pb-4 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 mb-1">
              <svg className="w-[18px] h-[18px] text-[#91918E] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
              </svg>
              <h2 className="text-base font-semibold text-[#37352F] truncate">转录文本预览</h2>
            </div>
            <span className="text-xs text-[#91918E] ml-7">{fileName}</span>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md text-[#91918E] hover:text-[#37352F] hover:bg-[#F7F7F5] transition-colors cursor-pointer flex-shrink-0 ml-4"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 工具栏 */}
        <div className="flex items-center gap-0.5 px-7 pb-3 border-b border-[#E9E9E7] flex-shrink-0">
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#73726E] hover:bg-[#F7F7F5] rounded-lg transition-colors cursor-pointer"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
            </svg>
            复制
          </button>
        </div>

        {/* 内容区域 */}
        {content ? (
          <article
            className="flex-1 overflow-y-auto px-7 py-5"
            style={{ scrollbarWidth: 'thin' }}
          >
            <div className="space-y-1 text-sm leading-relaxed">
              <Markdown
                components={{
                  p: ({ children }) => {
                    const text = String(children);
                    if (text.includes('**') || text.includes('*')) {
                      return null;
                    }
                    return (
                      <div className="pb-5 pt-1 leading-[1.8] text-[#37352F]">
                        {children}
                      </div>
                    );
                  },
                  strong: ({ children }) => {
                    const speaker = String(children);
                    let time = '';
                    const lines = content.split('\n');
                    for (const line of lines) {
                      if (line.includes(`**${speaker}`) && line.includes('<!-- TIME:')) {
                        const timeMatch = line.match(/<!--\s*TIME:([^>]+)\s*-->/);
                        if (timeMatch) {
                          time = timeMatch[1];
                        }
                        break;
                      }
                    }
                    return (
                      <div className="pt-3 flex items-center gap-2">
                        <span className="font-semibold text-sm text-[#37352F]">{speaker}</span>
                        {time && (
                          <span className="text-xs text-[#91918E]">{time}</span>
                        )}
                      </div>
                    );
                  },
                  em: () => null,
                }}
              >
                {content}
              </Markdown>
            </div>
          </article>
        ) : (
          <div className="flex-1 flex items-center justify-center py-20">
            <span className="text-sm text-[#91918E]">暂无内容</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TranscriptionPreviewModal;
